import styled, { css } from 'styled-components';
import { Button, Small, SmallBold } from '@comeon/ui';
import { ComponentPropsWithoutRef, FC } from 'react';
import { sizes } from '../../../utils/style-utils';
import { HTMLMotionProps } from 'motion/react';

/**
 * Component for displaying a label in a selection button. It is used together with SelectionButton.
 */
export const SelectionButtonLabel = styled(Small)`
	max-width: 100%; // Used when flex-direction is overridden

	color: var(--main-text-supportive);
	word-break: break-word;
`;

/**
 * Component for displaying odds in a selection button. It is used together with SelectionButton.
 */
export const SelectionButtonOdds = styled(SmallBold)`
	color: var(--main-text);
`;

const StyledButton = styled(Button)<{ isActive?: boolean }>`
	min-height: 37px; // according to design
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	border-radius: 6px;
	min-width: 0; // Added as a way to allow ellipsis for children
	overflow: hidden;
	padding: 2px 10px;

	background-color: var(--main-background);

	text-align: center;
	white-space: normal;

	${({ isActive }) =>
		isActive &&
		css`
			&& {
				background-color: var(--main-primary);
				${SelectionButtonLabel}, ${SelectionButtonOdds} {
					color: var(--main-on-primary);
				}
			}
		`}

	// Because some real devices don't support hover query, we need to use min-width as well.
	// https://www.ctrl.blog/entry/css-media-hover-samsung.html
	@media (hover: hover) and (min-width: ${sizes.xl}px) {
		&:hover {
			background-color: var(--main-primary-hover);
			${SelectionButtonLabel}, ${SelectionButtonOdds} {
				color: var(--main-on-primary);
			}

			${({ isActive }) =>
				isActive &&
				css`
					background-color: var(--main-primary-hover);
				`}
		}
	}

	&:disabled {
		background-color: var(--main-background);
		${SelectionButtonLabel}, ${SelectionButtonOdds} {
			opacity: 0.25;
			color: var(--main-text);
		}
	}
`;

type Props = {
	className?: string;
	isActive?: boolean;
	children?: React.ReactNode;
};

/**
 * Lightweight version of selection button. Open for styling and additional logic.
 * In most of the cases you should use it together with SelectionButtonLabel and SelectionButtonOdds.
 */
const SelectionButton: FC<Props & ComponentPropsWithoutRef<'button'> & HTMLMotionProps<'button'>> = ({
	className,
	isActive,
	children,
	...rest
}) => (
	<StyledButton isActive={isActive} className={className} {...rest}>
		{children}
	</StyledButton>
);

export default SelectionButton;
